import React, { createContext, useEffect, useState } from 'react'
import './i18n';  //// import i18n (needs to be bundled ;)) 
import { useTranslation } from 'react-i18next';
import { FaUser, FaUserPlus } from 'react-icons/fa';

import './resort-room-types.css';
import { useAppCtx } from './app-ctx';


const renderPeopleIcons = (count, isExtra = false) => {
  const icons = [];
  const IconComponent = isExtra ? FaUserPlus : FaUser;
  const iconColor = isExtra ? "#7f8c8d" : "#2c3e50";
  
  for (let i = 0; i < count; i++) {
    icons.push(
      <IconComponent 
        key={i} 
        color={iconColor} 
        size={16} 
        style={{ marginRight: 5 }}
      />
    );
  }
  
  return icons.length ? icons : <span style={{ color: '#ccc' }}>—</span>;
};


const BookPriceSelector = ({className, validationEerror, resortId, resortName, guests, dateRangeValue, setRoom, 
        updatePrices, defaultRooms, selectedRoomId}) => {
    const { t } = useTranslation();
    const {JOOMLA_VARS, CONFIG} = useAppCtx();

    const [ rooms, setRooms] = useState([]);
    const [ loaded, setLoaded] = useState(false);
    const [ apiError, setApiError] = useState(null);
    const [ pricesAvailable, setPricesAvailable] = useState(true); // true by default
    
    console.log(`BookPriceSelector() entry: resortId:${resortId}, ${JSON.stringify(guests)}, ${JSON.stringify(dateRangeValue)}` );

    React.useEffect(() => {
        const appRoot = JOOMLA_VARS.root;
        const apiPath = CONFIG.getPrices;

        const payload = {
                resort_id: resortId,
                guests:    guests,
                dates:     dateRangeValue   //{checkInDate: xxxx, checkOutDate: yyyy}
            };

        console.log('BookPriceSelector.useEffect(): payload:', JSON.stringify(payload));
        setApiError(null);

        // Abort the previous request when updatePrices changes (guests/dates
        // edited) or on unmount, so stale responses never overwrite newer ones.
        const controller = new AbortController();

        fetch(appRoot + apiPath, {
            method: 'POST',
            headers: {'Content-Type': 'application/json', 'X-CSRF-Token': JOOMLA_VARS.csrfToken || ''},
            body: JSON.stringify(payload),
            signal: controller.signal
        })
        .then(response => response.json())
        .then(data => { 
            console.log('BookPriceSelector.useEffect(): data:', JSON.stringify(data)); 

            // Handle API error message (existing behavior)
            if (data.apiErrmsg) {
                const errMsg = data.apiErrmsg.replace(new RegExp(resortId, 'g'), resortName);
                setApiError(errMsg);
            }

            // If backend explicitly says prices are unavailable → show default rooms
            if (data.prices && data.prices.pricesAvailable === false) {
                setPricesAvailable(false);
                setRooms(defaultRooms || []);
                setApiError('');
                return;
            }

            // If there's a general error → fall back to default rooms
            if (data.error) {
                setPricesAvailable(false);
                setRooms(defaultRooms || []);
                return;
            }

            // Success path — prices available (existing behavior)
            if (data.prices && data.prices.roomsWithPrices) {
                let roomsData = data.prices.roomsWithPrices;
                /*
                    * roomsData
                    *   [
                            {
                                "room_id":4568,
                                "resort_id":493,
                                "name":"Double Comfort (with balcon)",
                                "main":2,
                                "extra":1,
                                "room":4568,
                                "boards":"010101",
                                "price":"845.00",
                                "avail":0
                            }
                        ],


                */

                // Sort function
                roomsData.sort((a, b) => {
                  // First, compare avail: items with avail != 0 should come first
                  const availA = a.avail !== 0 ? 0 : 1; // 0 = higher priority
                  const availB = b.avail !== 0 ? 0 : 1;
                  
                  if (availA !== availB) {
                    return availA - availB;
                  }

                  // If same group, sort by price ascending
                  return parseFloat(a.price) - parseFloat(b.price);
                });

                setPricesAvailable(true);
                setRooms( roomsData );
                setApiError('');
            } else {
                // No roomsWithPrices returned → fall back to default rooms
                setPricesAvailable(false);
                setRooms(defaultRooms || []);
            }
        })
        .catch(error => {
            // Ignore cancellations caused by cleanup (unmount / re-fetch)
            if (error && error.name === 'AbortError') return;
            console.log('BookPriceSelector.useEffect(): error:', error);
        });

        return () => controller.abort();
    }, [updatePrices]);

    // The parent owns the selection (form.roomDetails) while the price list is
    // recalculated on every date/guest edit. When a refreshed list arrives, the
    // selected room must be handed to the parent again with its NEW price —
    // the card a user sees selected would otherwise indicate one price while a
    // stale price (the one from the moment of the selection) got submitted and
    // saved with the booking.
    React.useEffect(() => {
        if (!selectedRoomId || !setRoom) {
            return;
        }

        // Rooms of the "prices unavailable" fallback carry no price at all:
        // there is no indicated price to keep in sync then, so the current
        // selection is left untouched.
        const fresh = rooms.find((room) => String(room.room_id) === String(selectedRoomId)
            && room.price !== undefined);
        if (fresh) {
            setRoom(fresh);
        }
    }, [rooms]);

    const handleSetRoom = (room) => {
        console.log('BookPriceSelector().handleSetRoom(): newValue=', JSON.stringify(room));
        if (setRoom) {
            setRoom(room);
        }
    }

    return (
        <>
            <div className={`${className} room-types-container ${validationEerror ? 'error' : ''}`}>
                {rooms.map((room) => (
                    <div key={room.room_id} className={`room-card ${room.avail ? 'highlite' : ''}`}>
                        <label>
                            <input className='room-selector radio' type="radio" name="room" value={room.room_id} 
                                onChange={() => handleSetRoom(room)} />
                            <span className="people-icons">
                                {renderPeopleIcons(room.main, false)}
                                {renderPeopleIcons(room.extra, true)}
                            </span>
                            <span className="room-name">{t(room.name)}</span>
                            {'price' in room && (
                                <>
                                <span className='currency-symbol'>{room.currencySymbol}</span>
                                <span className="price">{room.price}</span>
                                <span className='currency-code'>{room.currency}</span>
                                </>
                            )}
                        </label>
                    </div>
                ))}
                {!pricesAvailable && (
                    <div className="no-prices-note">
                        {t('Prices are not available for the selected dates. Please contact us for pricing.')}
                    </div>
                )}
                {apiError && <div className="error-text api-error">{apiError}</div>}
            </div>
        </>
    )
}

export default BookPriceSelector;